import { mockUsers } from '../mockData';
import { store } from '../../store';
import { setToken, updateCurrentUser, logout as logoutAction } from '../../store/slices/authSlice';

const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// ProtectedRoute and baseApi read auth from redux, not localStorage — without
// seeding the store a mock login immediately bounces back to /login.
const dispatchMockSession = (user, token) => {
  const [givenName, ...rest] = (user.name || '').split(' ');
  store.dispatch(setToken({ token, refreshToken: null, expiresAt: Date.now() + 3600000 }));
  store.dispatch(updateCurrentUser({
    sub: String(user.id),
    given_name: givenName,
    family_name: rest.join(' '),
    email: user.email,
    role: user.role,
  }));
};

export const mockAuthService = {
  async login(email, password) {
    await delay(800);
    
    const user = mockUsers.find(u => u.email === email && u.password === password);
    
    if (!user) {
      throw new Error('Invalid email or password');
    }
    
    const { password: _, ...userWithoutPassword } = user;
    const token = 'mock-jwt-token-' + Date.now();
    
    localStorage.setItem('authToken', token);
    localStorage.setItem('currentUser', JSON.stringify(userWithoutPassword));
    dispatchMockSession(user, token);
    
    return {
      success: true,
      user: userWithoutPassword,
      token
    };
  },

  async loginWithGoogle() {
    await delay(1000);
    
    const googleUser = mockUsers.find(u => u.loginType === 'Federated' || u.loginType === 'Both');
    
    if (!googleUser) {
      throw new Error('No Google account linked');
    }
    
    const { password: _, ...userWithoutPassword } = googleUser;
    const token = 'mock-jwt-token-google-' + Date.now();
    
    localStorage.setItem('authToken', token);
    localStorage.setItem('currentUser', JSON.stringify(userWithoutPassword));
    dispatchMockSession(googleUser, token);
    
    return {
      success: true,
      user: userWithoutPassword,
      token
    };
  },

  async loginWithMicrosoft() {
    await delay(1000);
    
    const msUser = mockUsers.find(u => u.loginType === 'Federated' || u.loginType === 'Both');
    
    if (!msUser) {
      throw new Error('No Microsoft account linked');
    }
    
    const { password: _, ...userWithoutPassword } = msUser;
    const token = 'mock-jwt-token-ms-' + Date.now();
    
    localStorage.setItem('authToken', token);
    localStorage.setItem('currentUser', JSON.stringify(userWithoutPassword));
    dispatchMockSession(msUser, token);
    
    return {
      success: true,
      user: userWithoutPassword,
      token
    };
  },

  async exchangeExternalLogin() {
    await delay(500);
    const user = mockUsers.find(u => u.loginType === 'Federated' || u.loginType === 'Both');
    if (!user) throw new Error('No external login session');
    const { password: _, ...userWithoutPassword } = user;
    return { success: true, user: userWithoutPassword, token: 'mock-jwt-token-ext-' + Date.now() };
  },

  async exchangeAccountActivation() {
    await delay(500);
    const user = mockUsers[0];
    if (!user) throw new Error('No activation session');
    const { password: _, ...userWithoutPassword } = user;
    return { success: true, user: userWithoutPassword, token: 'mock-jwt-token-activate-' + Date.now() };
  },

  async impersonate() {
    throw new Error('Impersonation is not available in mock mode.');
  },

  exitImpersonation() {
    return null;
  },

  isImpersonating() {
    return false;
  },

  logout() {
    localStorage.removeItem('authToken');
    localStorage.removeItem('currentUser');
    store.dispatch(logoutAction());
  },

  getCurrentUser() {
    const userStr = localStorage.getItem('currentUser');
    return userStr ? JSON.parse(userStr) : null;
  },

  isAuthenticated() {
    return !!localStorage.getItem('authToken');
  },

  getToken() {
    return localStorage.getItem('authToken');
  }
};
